import { useState, useRef, useEffect } from "react";
import SortByModal from "../../../components/modals/SortByModal";
import useCompany from "../../../hooks/useCompany";
import { useStoreTheme } from "../../../store";
import { ClipLoader } from "react-spinners";
import { Button, Text } from "@oc-labs/wt-storybook";
import { Copy, Check } from "lucide-react";

function Members() {
	const { getCompanyUsers, activeCompany: company  } = useCompany();
	const { notificationSidebar } = useStoreTheme();

	const [membersData, setMembersData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const [searchQuery, setSearchQuery] = useState("");

	const [isSortModalOpen, setIsSortModalOpen] = useState(false);
	const [selectedSort, setSelectedSort] = useState("");

	const searchRef = useRef(null);
	const searchWrapperRef = useRef(null);
	const [showSearch, setShowSearch] = useState(false);

	const [copiedWallet, setCopiedWallet] = useState(null);

	const copyWalletToClipboard = async (wallet, index) => {
		try {
			await navigator.clipboard.writeText(wallet);
			setCopiedWallet(index);
			setTimeout(() => setCopiedWallet(null), 2000); 
		} catch (err) {
				console.error("Failed to copy wallet:", err);
			}
	};

	const handleSearchVisible = () => {
		setShowSearch((prev) => {
			const next = !prev;
			if (next && searchRef.current) {
				searchRef.current.focus();
			}
			return next;
		});
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				searchWrapperRef.current &&
				!searchWrapperRef.current.contains(event.target)
			) {
				setShowSearch(false);
			}
		};

		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);

	const normalizeMember = (member) => ({
			email: member.email || 'Not defined',
			name: member.name || 'Not defined',
			phone: member.phoneNumber || 'Not defined',
			wallet: member.wallet || 'Not defined',
			createdAt: member.createdAt,
			profile: {
					memberSince: member.createdAt,
					lastLogin: member.lastLogin
			}
	});

	useEffect(() => {
			if (!company?.id) return;
			setLoading(true);
			getCompanyUsers(company.id).then((users) => {
					let usersArray = Array.isArray(users) ? users : [];
					const normalizedUsers = usersArray.map(normalizeMember);
					setMembersData(normalizedUsers);
					setLoading(false);
					setTotalPages(1);
			}).catch(error => {
					console.error('[Members] Error fetching members:', error);
					setMembersData([]);
					setLoading(false);
			});
	}, [company?.id, page]);

const exportCSV = () => {
  try {
    if (!Array.isArray(membersData) || membersData.length === 0) {
      console.warn("[Members] No data to export");
      return;
    }

    const headers = [
      "Email",
      "Name",
      "Wallet",
      "Phone",
      "Member Since",
      "Last Login"
    ];

    const rows = membersData.map(member => [
      member.email || "Not defined",
      member.name || "Not defined",
      member.wallet || "Not defined",
      member.phone || "Not defined",
      member?.profile?.memberSince
        ? new Date(member.profile.memberSince).toLocaleString()
        : "Not defined",
      member?.profile?.lastLogin
        ? new Date(member.profile.lastLogin).toLocaleString()
        : "Never logged in"
    ]);

    const csvContent =
      [headers, ...rows]
        .map(row =>
          row
            .map(field => `"${String(field).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `members_export_${new Date().toISOString()}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

  } catch (error) {
    console.error("[Members] Error exporting CSV:", error);
  }
};

    if (loading || !company) {
			return (
				<div className="flex items-center justify-center w-full h-screen">
					<ClipLoader />
				</div>
			);
    }

    const filteredMembers = Array.isArray(membersData)
			? membersData.filter((member) => {
				const name = `${member.givenName ?? ""} ${member.familyName ?? ""}`.trim();
				return (
					member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
					name.toLowerCase().includes(searchQuery.toLowerCase())
				);
			})
		: [];

		return (
			<div className="2xl:p-8 p-4">
				<SortByModal
					isOpen={isSortModalOpen}
					closeModal={() => setIsSortModalOpen(false)}
					selectedSort={selectedSort}
					setSelectedSort={setSelectedSort}
				/>

				{/* Header & Actions */}
				<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
					<div>
						<h2 className="2xl:text-2xl text-lg font-bold">All Members</h2>
						<p className="text-slate-500">
							View and manage your community members.
						</p>
					</div>
					<div className="flex flex-wrap gap-3 items-center">
						<div className="flex items-center gap-2">
							<Button
								disabled={page === 1}
								variant="outline"
								size="small"
								onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
							>
								Previous
							</Button>
							<Text className="text-xs text-slate-500">
								{page}/{totalPages}
							</Text>
							<Button
								disabled={page === totalPages}
								variant="outline"
								size="small"
								onClick={() => setPage((prev) => prev + 1)}
							>
								Next
							</Button>
						</div>
						<div className="relative" ref={searchWrapperRef}>
							<input
								ref={searchRef}
								type="text"
								placeholder="Search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								onFocus={() => setShowSearch(true)}
								className={`pl-10 pr-4 py-[6px] h-[32px] bg-gray-100 rounded-full text-sm ${showSearch ? "w-72" : "w-32"
									} outline-none transition-all duration-300`}
							/>
							<img
								src="/icons/search.svg"
								alt="Search"
								className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer opacity-50"
								onClick={handleSearchVisible}
							/>
						</div>
						<Button size="small" variant="secondary" onClick={exportCSV}>
							Export CSV
						</Button>
					</div>
				</div>

				{/* Table */}
				<div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
					<table className="w-full text-left border-collapse table-fixed">
						<thead>
							<tr className="bg-slate-50 border-b border-slate-200">
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[30%]">
									Member
								</th>
								{!notificationSidebar && (
									<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[13%]">
										Phone
									</th>
								)}
								{!notificationSidebar && (
									<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[13%]">
										Member Since
									</th>
								)}
								{!notificationSidebar && (
									<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[13%]">
										Last Login
									</th>
								)}
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Public Key
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{filteredMembers.map((member, index) => (
								<tr
									key={index}
									className="hover:bg-slate-50/50 transition-colors"
								>
									<td className="px-6 py-4">
										<div className="flex items-center gap-3 min-w-0">
											<div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
												{member.avatar ? (
													<img
														alt={`${member.name} Profile`}
														src={member.avatar}
														className="w-full h-full object-cover"
													/>
												) : (
													<div className="w-full h-full flex items-center justify-center text-slate-500 font-semibold">
														{member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || "U"}
													</div>
												)}
											</div>
											<div className="min-w-0">
												<div className="font-semibold text-sm truncate">{member?.name || "Not defined"}</div>
												<div className="text-xs text-slate-500 truncate">{member?.email || "Not defined"}</div>
											</div>
										</div>
									</td>
									{!notificationSidebar && (
										<td className="px-6 py-4">
											<span className="text-sm text-slate-700 truncate block">{member.phone || "Not defined"}</span>
										</td>
									)}
									{!notificationSidebar && (
										<td className="px-6 py-4">
											<span className="text-sm text-slate-700 truncate block">{new Date(member?.profile?.memberSince).toLocaleDateString()}</span>
										</td>
									)}
									{!notificationSidebar && (
										<td className="px-6 py-4">
											<span className="text-sm text-slate-700 truncate block">
												{member?.profile?.lastLogin
													? new Date(member?.profile?.lastLogin).toLocaleDateString()
													: <span className="text-slate-400">Never</span>
												}
											</span>
										</td>
									)}
									<td className="px-6 py-4">
										<div className="flex items-center gap-2 min-w-0">
											<button
												onClick={() => copyWalletToClipboard(member.wallet, index)}
												className="p-1.5 rounded hover:bg-slate-100 transition-colors cursor-pointer flex-shrink-0"
												title="Copy public key"
											>
												{copiedWallet === index ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
											</button>
											<span className="font-mono text-xs text-slate-600 truncate">{member.wallet}</span>
										</div>
									</td>
								</tr>
							))}
							{filteredMembers.length === 0 && (
								<tr>
									<td colSpan={6} className="py-8 px-6 text-center text-slate-400 text-sm">
										No members found.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
		</div>
		);
}

export default Members;
